import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Modal } from './ui/Modal'; // Assuming Modal accepts translated title/footer
import { Input } from './ui/Input'; // Assuming Input accepts translated label/error
import { Button } from './ui/Button'; // Assuming Button accepts translated text
import { Dropdown } from './ui/Dropdown'; // Assuming Dropdown accepts translated label/options
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';
import {
  Patient,
  SessionPriceTier,
  BillingAddress,
} from '../types/firestoreTypes';
import { PATIENT_STATUS } from '../types/constants';

type PatientSaveData = Omit<Patient, 'id' | 'therapistId' | 'createdAt'>;

interface AddNewPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PatientSaveData) => Promise<void>; // Keep Promise<void>
  priceTiers: SessionPriceTier[];
}

// Define the initial state structure more clearly
const initialFormData: PatientSaveData = {
  name: '',
  email: '',
  phone: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
    country: '',
  },
  status: 'Prospect', // Default status
  defaultPriceTierId: '', // Default empty
};

// Type for errors state to hold translation keys
type ErrorState = Partial<Record<string | 'submit', string>>; // Allow 'submit' key

const AddNewPatientModal: React.FC<AddNewPatientModalProps> = ({
  isOpen,
  onClose,
  onSave,
  priceTiers,
}) => {
  const { t } = useTranslation(); // Get the t function
  const { trackEvent } = useAnalytics();
  const { therapist } = useAuth();
  const [formData, setFormData] = useState<PatientSaveData>(initialFormData);
  const [errors, setErrors] = useState<ErrorState>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset form when modal opens/closes or price tiers change (to repopulate dropdown)
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setErrors({});
      setIsSubmitting(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form and set error keys
  const validateForm = () => {
    const newErrors: ErrorState = {};

    if (!formData.name.trim()) {
      newErrors.name = 'patientModal.errors.nameRequired';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'patientModal.errors.emailRequired';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'patientModal.errors.emailInvalid';
    }
    if (!formData.status) {
      // Should always have a default, but check anyway
      newErrors.status = 'patientModal.errors.statusRequired';
    }
    // Price tier is no longer required - will auto-assign therapist's primary tier if not selected

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous submit errors
    try {
      // Auto-assign therapist's primary tier if no tier selected
      const finalFormData = {
        ...formData,
        defaultPriceTierId: formData.defaultPriceTierId || therapist?.primaryPriceTierId || ''
      };

      await onSave(finalFormData);

      // Track successful patient creation
      trackEvent('patient_created', {
        status: formData.status,
      });

      setHasUnsavedChanges(false); // Mark changes as saved
      handleClose(true); // Force close without confirmation
    } catch (error) {
      console.error('Error saving patient:', error);
      
      // Track the error
      trackEvent('error_occurred', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        context: 'patient_creation',
      });

      // Set a generic submit error key
      setErrors({ submit: 'patientModal.errors.submitFailed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes for nested state
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => {
      // Use structuredClone for safer deep updates
      const newData = structuredClone(prev);
      const fields = field.split('.');
      let current: any = newData;

      for (let i = 0; i < fields.length - 1; i++) {
        // Ensure nested objects exist
        if (!current[fields[i]]) current[fields[i]] = {};
        current = current[fields[i]];
      }
      current[fields[fields.length - 1]] = value;

      return newData;
    });
    setHasUnsavedChanges(true);

    // Clear specific field error on change
    if (errors[field] || errors.submit) {
      // Also clear submit error on any change
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors.submit; // Clear submit error as well
        return newErrors;
      });
    }
  };

  // Handle modal closing with confirmation for unsaved changes
  const handleClose = (forceClose = false) => {
    if (hasUnsavedChanges && !forceClose) {
      // Use translated confirmation message
      if (window.confirm(t('patientModal.confirmations.unsavedChanges'))) {
        // Reset state only if user confirms
        setFormData(initialFormData);
        setErrors({});
        setHasUnsavedChanges(false);
        onClose(); // Call the passed onClose handler
      }
    } else {
      // No unsaved changes or force close, just reset and close
      setFormData(initialFormData);
      setErrors({});
      setHasUnsavedChanges(false);
      onClose(); // Call the passed onClose handler
    }
  };

  // Translate status options
  const statusOptions = [
    { value: PATIENT_STATUS.PROSPECT, label: t('patients.status.PROSPECT') },
    {
      value: PATIENT_STATUS.ONBOARDING,
      label: t('patients.status.ONBOARDING'),
    },
    { value: PATIENT_STATUS.ACTIVE, label: t('patients.status.ACTIVE') },
    { value: PATIENT_STATUS.CLOSED, label: t('patients.status.CLOSED') },
  ];

  // Generate price tier options with translated default
  const primaryTier = priceTiers.find(tier => tier.id === therapist?.primaryPriceTierId);
  const priceTierOptions = [
    {
      value: '',
      label: primaryTier
        ? t('patientModal.form.useDefaultTier', { tierName: primaryTier.tierName })
        : t('patientModal.form.selectPriceTier')
    }, // Translated default with primary tier name
    ...priceTiers.map((tier) => ({
      value: tier.id,
      // Keep tier name and details as is (assuming they are user-defined)
      // Future enhancement: Localize currency formatting
      label: `${tier.tierName} (${tier.currency} ${tier.price.toFixed(2)})${
        tier.id === therapist?.primaryPriceTierId ? ` - ${t('patientModal.form.primary')}` : ''
      }`,
    })),
  ];

  // Translate modal footer buttons
  const modalFooter = (
    <div className="flex justify-end gap-3">
      {' '}
      {/* Added gap */}
      <Button
        variant="secondary"
        onClick={() => handleClose()}
        disabled={isSubmitting}
      >
        {t('patientModal.buttons.cancel')}
      </Button>
      <Button
        type="submit" // Make this the submit button for the form
        form="add-patient-form" // Link button to the form
        variant="primary"
        // onClick={handleSubmit} // Remove onClick, use form onSubmit
        disabled={isSubmitting || !hasUnsavedChanges} // Also disable if no changes
        isLoading={isSubmitting}
      >
        {t('patientModal.buttons.addPatient')}
      </Button>
    </div>
  );

  // Helper to translate error messages from state
  const getErrorMessage = (field: string): string | undefined => {
    const errorKey = errors[field];
    return errorKey ? t(errorKey) : undefined;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => handleClose()} // Use the wrapper handleClose
      title={t('patientModal.title')} // Translate title
      size="lg" // Consider responsive sizing
      footer={modalFooter} // Pass translated footer
    >
      {/* Add id to form and use onSubmit */}
      <form
        id="add-patient-form"
        className="space-y-6"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Display translated submit error */}
        {getErrorMessage('submit') && (
          <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm border border-red-200">
            {getErrorMessage('submit')}
          </div>
        )}

        {/* Top Row: Status & Price Tier */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Dropdown
            label={t('patientModal.form.status')}
            options={statusOptions} // Translated options
            value={formData.status}
            onChange={(value) =>
              handleInputChange('status', value as Patient['status'])
            }
            error={getErrorMessage('status')} // Translate error
            required
          />
          <Dropdown
            label={t('patientModal.form.defaultPriceTier')}
            options={priceTierOptions} // Options with translated default
            value={formData.defaultPriceTierId}
            onChange={(value) => handleInputChange('defaultPriceTierId', value)}
            error={getErrorMessage('defaultPriceTierId')} // Translate error
            required
          />
        </div>

        {/* Basic Information Section */}
        <div>
          <h3 className="text-base font-medium text-[#344054] mb-4 border-b pb-2">
            {' '}
            {/* Adjusted styling */}
            {t('patientModal.headings.basicInfo')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('patientModal.form.name')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={getErrorMessage('name')} // Translate error
              required
              placeholder={t('patientModal.placeholders.name')}
            />
            <Input
              label={t('patientModal.form.email')}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={getErrorMessage('email')} // Translate error
              required
              placeholder={t('patientModal.placeholders.email')}
            />
            <Input
              label={t('patientModal.form.phone')}
              type="tel"
              value={formData.phone || ''} // Ensure value is string
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder={t('patientModal.placeholders.phone')}
            />
          </div>
        </div>

        {/* Address Section */}
        <div>
          <h3 className="text-base font-medium text-[#344054] mb-4 border-b pb-2">
            {' '}
            {/* Adjusted styling */}
            {t('patientModal.headings.address')}
          </h3>
          <div className="space-y-4">
            <Input
              label={t('patientModal.form.street')}
              value={formData.address?.street || ''} // Safe access
              onChange={(e) =>
                handleInputChange('address.street', e.target.value)
              }
              placeholder={t('patientModal.placeholders.street')}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label={t('patientModal.form.city')}
                value={formData.address?.city || ''} // Safe access
                onChange={(e) =>
                  handleInputChange('address.city', e.target.value)
                }
                placeholder={t('patientModal.placeholders.city')}
              />
              <Input
                label={t('patientModal.form.postalCode')}
                value={formData.address?.postalCode || ''} // Safe access
                onChange={(e) =>
                  handleInputChange('address.postalCode', e.target.value)
                }
                placeholder={t('patientModal.placeholders.postalCode')}
              />
              <Input
                label={t('patientModal.form.country')}
                value={formData.address?.country || ''} // Safe access
                onChange={(e) =>
                  handleInputChange('address.country', e.target.value)
                }
                placeholder={t('patientModal.placeholders.country')}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddNewPatientModal;
